/**
 * useCampaignSimulation
 *
 * Drives leads through each campaign's configured sequence. Each tick:
 *   1. For steps in REVERSE order: advance leads whose currentStepIndex matches
 *      that step index (prevents double-advancing in a single tick).
 *   2. Leads that completed the full sequence get a chance to reply.
 *   3. Replied leads become converted or skipped.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useCampaignsRequest } from '../../../services/apis/campaigns/useCampaignsRequest';
import { useCampaignExecutor } from './useCampaignExecutor';
import { useAuthStore } from '../../../stores/auth/useAuthStore';
import { ISequenceStep, STEP_STATUS, DEFAULT_SEQUENCE, DEFAULT_AI_LANGUAGE, expectedStatusForStep, FINAL_LEAD_STATUSES, isAwaitingCampaignMessageReply, isOutboundLinkedInOpener, campaignMayHaveAwaitingReplies, getLeadStepContentOverride, isPerLeadAiComment, renderCampaignTemplate } from '../../../interfaces/models/campaign';

const randInt = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;
const pick    = (prob: number)             => Math.random() < prob;

// How long to wait for an invite to be accepted before failing the lead.
// Mirrors the cloud executor's INVITE_ACCEPTANCE_TIMEOUT_DAYS.
const ACCEPTANCE_TIMEOUT_DAYS =
  parseInt(process.env.REACT_APP_INVITE_ACCEPTANCE_TIMEOUT_DAYS ?? '14', 10);

// Min gap between first-degree polls for the same lead (avoid hammering LN).
const ACCEPT_CHECK_INTERVAL_MS = 3 * 60 * 1000;

// Post steps depend on the lead having published something — a condition the
// campaign cannot control. Such a step is skipped, never failed, and never left
// retrying forever: the rest of the sequence (invite, message) still runs.
// Mirrors the cloud executor's POST_STEP_TYPES / NO_POST_REASONS handling.
const POST_STEP_TYPES = ['post_like', 'post_comment'];

// Verdicts a retry cannot change. They still get one grace attempt, because the
// profile-posts call occasionally comes back empty for a member who does post.
const NO_POST_REASONS = ['no_posts_found'];
const POST_STEP_GRACE_ATTEMPTS = 2;
const MAX_POST_STEP_ATTEMPTS   = 5;

const SKIP_REASON_TEXT: Record<string, string> = {
  no_posts_found:             'the lead has no posts',
  post_like_failed:           'LinkedIn rejected the like',
  post_comment_failed:        'LinkedIn rejected the comment',
  profile_lookup_failed:      'the LinkedIn profile could not be opened',
  profile_posts_fetch_failed: 'the posts could not be fetched',
  ai_generation_failed:       'the comment could not be generated',
};

/**
 * Counts a post step's consecutive failures. 'retry' while it still has attempts
 * left, 'skip' once they run out — a post step is never failed and never retried
 * forever.
 */
function registerPostStepFailure(
  attempts: Map<string, number>,
  leadId:   string,
  stepIdx:  number,
  reason:   string,
): 'retry' | 'skip' {
  const key    = `${leadId}:${stepIdx}`;
  const count  = (attempts.get(key) ?? 0) + 1;
  const budget = NO_POST_REASONS.includes(reason) ? POST_STEP_GRACE_ATTEMPTS : MAX_POST_STEP_ATTEMPTS;

  if (count < budget) {
    attempts.set(key, count);
    console.warn(`[Simulation] post step ${stepIdx} failed for lead ${leadId} (${reason}) — attempt ${count}/${budget}`);
    return 'retry';
  }
  attempts.delete(key);
  return 'skip';
}

function postStepSkipMessage(stepType: string, reason: string) {
  const label = stepType === 'post_like' ? 'Like' : 'Comment';
  return {
    stepType,
    content: `${label} step skipped — ${SKIP_REASON_TEXT[reason] ?? reason.replace(/_/g, ' ')}.`,
    direction: 'system' as const,
  };
}

function renderTpl(
  tpl: string,
  lead: any,
  senderName = '',
  fallbacks: Record<string, string> = {},
): string {
  return renderCampaignTemplate(tpl, lead, senderName, fallbacks);
}

function stepMessage(
  step: ISequenceStep,
  lead: any,
  senderName = '',
  stepIndex = 0,
  fallbacks: Record<string, string> = {},
): string {
  const override = getLeadStepContentOverride(lead, stepIndex);
  if (override?.trim()) return renderTpl(override, lead, senderName, fallbacks) || '—';
  if (step.type === 'message') {
    if (step.finalContent?.trim()) return renderTpl(step.finalContent, lead, senderName, fallbacks);
    const tpl = step.messageMode === 'template' ? step.messageTemplate : step.messageAiPrompt;
    return renderTpl(tpl ?? '', lead, senderName, fallbacks) || '—';
  }
  if (step.type === 'post_comment') {
    // AI comments are generated per lead against their own post — a builder-time
    // finalContent must never be sent to everyone (see isPerLeadAiComment).
    if (!isPerLeadAiComment(step) && step.finalContent?.trim()) {
      return renderTpl(step.finalContent, lead, senderName, fallbacks);
    }
    const tpl = step.commentMode === 'template' ? step.commentTemplate : step.commentAiPrompt;
    return renderTpl(tpl ?? '', lead, senderName, fallbacks) || '—';
  }
  if (step.type === 'connection_request') {
    if (!step.includeNote && !step.finalContent?.trim() && !step.messageTemplate?.trim() && !step.messageAiPrompt?.trim()) {
      return 'Connection request sent';
    }
    if (step.finalContent?.trim()) return renderTpl(step.finalContent, lead, senderName, fallbacks);
    const tpl = step.messageMode === 'template' ? step.messageTemplate : step.messageAiPrompt;
    const note = renderTpl(tpl ?? '', lead, senderName, fallbacks);
    return note.trim() || 'Connection request sent';
  }
  if (step.type === 'profile_visit')      return 'Visited the profile';
  if (step.type === 'post_like')          return 'Liked the post';
  return '—';
}

export const SIM_EVENT = 'campaignSimTick';

/** UI poll interval for proxy campaigns (cloud executor / Unipile update DB only — no SIM_EVENT). */
export const PROXY_UI_POLL_MS = 45_000;

function sumLeadCounts(counts?: Record<string, number>): number {
  if (!counts) return 0;
  return Object.values(counts).reduce((sum, n) => sum + (Number(n) || 0), 0);
}

/** Best-effort timestamp of our last campaign DM / invite note (outbound row or DB field). */
function getLeadMessageSentAtMs(lead: any): number | null {
  const outbound = (lead.messages ?? []).filter(isOutboundLinkedInOpener);
  const lastOutbound = outbound[outbound.length - 1];
  if (lastOutbound?.sentAt) {
    const t = new Date(lastOutbound.sentAt).getTime();
    if (!Number.isNaN(t)) return t;
  }
  if (lead.messageSentAt) {
    const t = new Date(lead.messageSentAt).getTime();
    if (!Number.isNaN(t)) return t;
  }
  if (lead.lastContactedAt) {
    const t = new Date(lead.lastContactedAt).getTime();
    if (!Number.isNaN(t)) return t;
  }
  return null;
}

/** Active campaigns with enrolled leads, or paused campaigns awaiting reply / acceptance polling.
 *  Only campaigns created by the current user are executed — the extension acts through THIS
 *  user's LinkedIn session, so running another member's campaign would send in their name. */
const HOUR_MS = 60 * 60 * 1000;

/**
 * When a lead's first step may run, or null when nothing holds it back.
 *
 * Every other step's wait is scheduled (by the backend) when the step before it
 * completes; nothing does that for step 0, so a new lead arrives with no nextStepAt.
 * Its wait counts from the campaign's (re)start, or from enrollment when the lead
 * joined a campaign that was already running. Only leads the campaign has not
 * touched qualify — a retried lead carries a system message, so it is not held again.
 * Mirrors firstStepDueAt in the cloud executor, which runs proxy campaigns.
 */
function firstStepDueAt(campaign: any, sequence: ISequenceStep[], lead: any): number | null {
  const first = sequence[0];
  if (!first || (lead.currentStepIndex ?? 0) !== 0) return null;
  if (lead.nextStepAt || lead.lastContactedAt || lead.messages?.length) return null;
  const delayMs = Math.max(0, (Number(first.delayDays) || 0) * 24 * HOUR_MS + (Number(first.delayHours) || 0) * HOUR_MS);
  const from = Math.max(
    campaign.runTimer?.startedAt ? new Date(campaign.runTimer.startedAt).getTime() : 0,
    lead.enrolledAt ? new Date(lead.enrolledAt).getTime() : 0,
  );
  if (!delayMs || !from) return null;
  return from + delayMs;
}

function simulationTargets(campaigns: any[], currentUserId?: string | null): any[] {
  return campaigns.filter(c => {
    if (!currentUserId || String(c.createdBy) !== String(currentUserId)) return false;
    if (c.executionMethod === 'proxy') return false;
    if (c.status !== 'active' && c.status !== 'paused') return false;
    const total = sumLeadCounts(c.statusCounts);
    const messageSent = c.statusCounts?.message_sent ?? 0;
    const inviteSent = c.statusCounts?.invite_sent ?? 0;
    if (c.status === 'active') return total > 0 || messageSent > 0 || inviteSent > 0;
    if (c.status === 'paused') {
      return messageSent > 0 || inviteSent > 0 || campaignMayHaveAwaitingReplies(c.sequence, c.statusCounts);
    }
    return false;
  });
}

// ─── Multi-tab leader election ───────────────────────────────────────────────
// Only one tab may drive campaign execution: without this, two open tabs both
// pass the daily-limit pre-check and send the same LinkedIn action twice before
// the first PATCH lands. A localStorage heartbeat lock elects a single leader;
// if the leader tab closes or hangs, the lock expires and another tab takes over.

const LEADER_KEY = 'ld-campaign-sim-leader';
const TAB_ID     = Math.random().toString(36).slice(2) + Date.now().toString(36);

function acquireLeadership(ttlMs: number): boolean {
  try {
    const raw = localStorage.getItem(LEADER_KEY);
    if (raw) {
      const { id, ts } = JSON.parse(raw);
      if (id !== TAB_ID && Date.now() - ts < ttlMs) return false; // another live leader
    }
    localStorage.setItem(LEADER_KEY, JSON.stringify({ id: TAB_ID, ts: Date.now() }));
    return true;
  } catch {
    return true; // localStorage unavailable — fail open (single-tab assumption)
  }
}

function releaseLeadership(): void {
  try {
    const raw = localStorage.getItem(LEADER_KEY);
    if (raw && JSON.parse(raw).id === TAB_ID) localStorage.removeItem(LEADER_KEY);
  } catch { /* ignore */ }
}

export interface ISimGlobalConfig {
  enabled:               boolean;
  minIntervalSeconds:    number;
  maxIntervalSeconds:    number;
  minLeadsPerCycle:      number;
  maxLeadsPerCycle:      number;
  replyProbability:      number;
  conversionProbability: number;
}

// Live extension mode (REACT_APP_SIM_HIDE_UI): real LinkedIn outbound actions — no fake inbound replies by default.
const LIVE_EXTENSION = !!process.env.REACT_APP_SIM_HIDE_UI;

export const DEFAULT_SIM_CONFIG: ISimGlobalConfig = {
  enabled:               true,
  minIntervalSeconds:    parseInt(process.env.REACT_APP_SIM_INTERVAL_MIN ?? '30',  10),
  maxIntervalSeconds:    parseInt(process.env.REACT_APP_SIM_INTERVAL_MAX ?? '90',  10),
  minLeadsPerCycle:      parseInt(process.env.REACT_APP_SIM_LEADS_MIN    ?? '2',   10),
  maxLeadsPerCycle:      parseInt(process.env.REACT_APP_SIM_LEADS_MAX    ?? '5',   10),
  replyProbability:      process.env.REACT_APP_SIM_REPLY_PROB !== undefined
    ? parseFloat(process.env.REACT_APP_SIM_REPLY_PROB)
    : (LIVE_EXTENSION ? 0 : 0.6),
  conversionProbability: process.env.REACT_APP_SIM_CONV_PROB !== undefined
    ? parseFloat(process.env.REACT_APP_SIM_CONV_PROB)
    : (LIVE_EXTENSION ? 0 : 0.5),
};

export function useCampaignSimulation(config: ISimGlobalConfig = DEFAULT_SIM_CONFIG) {
  const { getCampaigns, getCampaignLeads, updateLeadStatus, generateCampaignMessage, updateCampaign } = useCampaignsRequest();
  const { execute: executeStep, checkLeadFirstDegree, checkLeadMessageReply, fetchLeadLatestPost } = useCampaignExecutor();
  const { store: { user } } = useAuthStore();
  const cfgRef    = useRef(config);
  const timerRef  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeRef = useRef(false);
  const acceptCheckTsRef = useRef<Map<string, number>>(new Map());
  // `${leadId}:${stepIdx}` → consecutive failures of that post step, so a step
  // can be retried a few ticks before it is skipped.
  const postStepAttemptsRef = useRef<Map<string, number>>(new Map());
  const runTickRef = useRef<() => void>(() => {});
  const userIdRef = useRef<string | undefined>(user?._id);
  const senderNameRef = useRef('');

  useEffect(() => { cfgRef.current = config; }, [config]);
  useEffect(() => { userIdRef.current = user?._id; }, [user?._id]);
  useEffect(() => {
    senderNameRef.current = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
  }, [user?.firstName, user?.lastName]);

  const scheduleTick = useCallback(() => {
    const cfg   = cfgRef.current;
    const minS  = Math.max(30, cfg.minIntervalSeconds || 30);
    const maxS  = Math.max(minS, cfg.maxIntervalSeconds || minS);
    const delay = randInt(minS, maxS) * 1000;
    timerRef.current = setTimeout(() => { void runTickRef.current(); }, delay);
  }, []);

  const runTick = useCallback(async () => {
    if (!activeRef.current) return;
    const cfg = cfgRef.current;

    // Leader lock TTL: two full intervals + slack, so a slow tick doesn't lose leadership
    const ttlMs = (cfg.maxIntervalSeconds * 2 + 60) * 1000;
    if (!acquireLeadership(ttlMs)) {
      if (activeRef.current) scheduleTick();
      return;
    }

    try {
      const data = await getCampaigns();
      const targets = simulationTargets(data?.campaigns ?? [], userIdRef.current);

      if (!targets.length) {
        if (activeRef.current) scheduleTick();
        return;
      }

      const cfg = cfgRef.current;
      const senderName = senderNameRef.current;
      const totalPool   = randInt(cfg.minLeadsPerCycle, cfg.maxLeadsPerCycle);
      const perCampaign = Math.max(1, Math.floor(totalPool / targets.length));
      let uiDirty = false;

      const activeCount = targets.filter((c: any) => c.status === 'active').length;
      if (activeCount > 0) {
        console.log(`[Simulation] tick — ${activeCount} active campaign(s), pool: ${totalPool}, per campaign: ${perCampaign}`);
      }

      for (const campaign of targets) {
          const sequence: ISequenceStep[] = campaign.sequence?.length ? campaign.sequence : DEFAULT_SEQUENCE;
          const variableFallbacks = campaign.variableFallbacks ?? {};
          const isActive = campaign.status === 'active';

          const leadsData  = await getCampaignLeads(campaign._id, { limit: 500 }, { sim: true });
          const leads: any[] = leadsData?.leads ?? [];

          // Auto-stop: if all enrolled leads are in a final status (converted/closed_lost/failed/suppressed),
          // pause the campaign automatically so it doesn't keep running.
          if (isActive && leads.length > 0) {
            const allOutcome = leads.every(l => FINAL_LEAD_STATUSES.includes(l.status));
            if (allOutcome) {
              console.log(`[Simulation] campaign ${campaign._id} has only outcome leads — auto-pausing`);
              await updateCampaign(campaign._id, { status: 'paused' });
              uiDirty = true;
              continue;
            }
          }

          const now    = Date.now();
          const ready  = (l: any) => {
            if (l.nextStepAt) return new Date(l.nextStepAt).getTime() <= now;
            const firstDueAt = firstStepDueAt(campaign, sequence, l);
            return firstDueAt == null || firstDueAt <= now;
          };

          // Leads waiting for invite acceptance: invite_sent at message step, or
          // invite-only campaigns that finished the sequence.
          const isAwaitingAcceptance = (l: any): boolean => {
            if (l.status !== 'invite_sent') return false;
            const stepIdx = l.currentStepIndex ?? 0;
            const hadInvite = sequence.some(s => s.type === 'connection_request');
            if (!hadInvite) return false;
            const atMessageStep = stepIdx < sequence.length && sequence[stepIdx]?.type === 'message';
            const inviteOnlyDone = stepIdx >= sequence.length && !sequence.some(s => s.type === 'message');
            return atMessageStep || inviteOnlyDone;
          };

          const shouldPollAcceptance = (leadId: string): boolean => {
            const last = acceptCheckTsRef.current.get(leadId) ?? 0;
            return Date.now() - last >= ACCEPT_CHECK_INTERVAL_MS;
          };

          // ── invite_sent → accepted (poll LinkedIn, throttled) ───────────
          // Runs for paused campaigns too: an invite already sent can be accepted while
          // the campaign is paused, and simulationTargets keeps such campaigns in the
          // tick precisely so that acceptance still gets recorded.
          const awaitingAccept = leads
            .filter(l => isAwaitingAcceptance(l) && ready(l) && shouldPollAcceptance(l._id))
            // Least-recently-checked first, so the per-campaign slice rotates through
            // every lead. In plain array order the head of the list falls due again
            // sooner than the slice can reach the tail, and the tail is never polled.
            .sort((a, b) =>
              (acceptCheckTsRef.current.get(a._id) ?? 0) - (acceptCheckTsRef.current.get(b._id) ?? 0))
            .slice(0, perCampaign);

          for (const lead of awaitingAccept) {
            // Mark only leads that are actually selected for this tick. Marking inside
            // the filter would throttle every candidate before the per-campaign slice,
            // permanently starving leads outside the first batch.
            acceptCheckTsRef.current.set(lead._id, Date.now());
            if (LIVE_EXTENSION) {
              const isAccepted = await checkLeadFirstDegree(campaign._id, lead);
              if (isAccepted === true) {
                console.log(`[Simulation] lead ${lead._id} accepted the connection request`);
                acceptCheckTsRef.current.delete(lead._id);
                await updateLeadStatus(campaign._id, lead._id, 'accepted', undefined, {
                  stepType: 'connection_request',
                  content: 'Connection request accepted',
                  direction: 'inbound',
                });
                uiDirty = true;
              }
            } else {
              // Dev/sim without extension: auto-accept so the message step can proceed.
              await updateLeadStatus(campaign._id, lead._id, 'accepted', undefined, {
                stepType: 'connection_request',
                content: 'Connection request accepted (simulated)',
                direction: 'inbound',
              });
              uiDirty = true;
            }
          }

          if (isActive) {

          // Expected lead status before executing step[idx] (see expectedStatusForStep).
          const expectedStatus = (idx: number): string => expectedStatusForStep(idx, sequence);

          // Dedup guard: a lead processed in one iteration must not be touched again this tick
          const processed = new Set<string>();

          // Step types that hit the daily limit this tick — skip their remaining
          // leads without re-calling the limit-check API for each one
          const limitedTypes = new Set<string>();

          const byStep = (idx: number) => {
            const expected = expectedStatus(idx);
            return leads.filter(l =>
              !processed.has(l._id) &&
              (l.currentStepIndex ?? 0) === idx &&
              l.status === expected &&
              ready(l)
            );
          };

          // ── Advance through sequence steps (reverse order) ──────────────
          for (let stepIdx = sequence.length - 1; stepIdx >= 0; stepIdx--) {
            const step       = sequence[stepIdx];
            const nextStatus = STEP_STATUS[step.type];
            if (!nextStatus) continue;
            if (limitedTypes.has(step.type)) continue;

            // Email steps belong to the cloud executor — the browser can't send mail.
            // Bailing out here (rather than at execute time) also avoids burning an AI
            // generation call on content this side will never send.
            if (step.type === 'email') continue;

            const toAdvance = byStep(stepIdx).slice(0, perCampaign);
            for (const lead of toAdvance) {
              processed.add(lead._id);

              const needsPerLeadAi = !getLeadStepContentOverride(lead, stepIdx)?.trim()
                && (
                  // AI comments always regenerate per lead, even when the step carries
                  // a finalContent sample from the builder — it was written against a
                  // sample post, not this lead's.
                  isPerLeadAiComment(step)
                  || (!step.finalContent?.trim()
                    && step.type === 'connection_request' && !!step.includeNote && step.messageMode === 'ai')
                );

              // Resend guard. Message records carry no step index, so this counts
              // occurrences instead: the Nth step of a type may only run while the lead
              // has fewer than N outbound records of that type. Matching on the type
              // alone made the SECOND step of any repeated type permanently unreachable
              // — and the builder allows two messages, likes and comments per campaign
              // (MAX_STEPS_PER_TYPE) — leaving the lead parked on that step forever,
              // because skipping here never advances currentStepIndex.
              const occurrence = sequence
                .slice(0, stepIdx + 1)
                .filter((s: ISequenceStep) => s.type === step.type).length;
              const sentOfType = (lead.messages ?? []).filter(
                (m: any) => m.stepType === step.type && m.direction === 'outbound',
              ).length;
              if (sentOfType >= occurrence) {
                console.warn(`[Simulation] lead ${lead._id} already has ${sentOfType} outbound ${step.type} record(s) for occurrence ${occurrence} — skipping resend`);
                continue;
              }

              // ── Never follow up on a lead who already answered ──
              // Reply polling runs after this loop and is throttled per lead, so a reply
              // to the invite note (or to an earlier DM) can still be unseen here. Check
              // the thread right before sending; a reply stops the sequence instead.
              if (
                LIVE_EXTENSION
                && (step.type === 'message' || step.type === 'sn_message')
                && (lead.messages ?? []).some(isOutboundLinkedInOpener)
              ) {
                const sinceMs = getLeadMessageSentAtMs(lead);
                const reply = sinceMs ? await checkLeadMessageReply(lead, sinceMs, { force: true }) : null;
                if (reply?.text) {
                  console.log(`[Simulation] lead ${lead._id} already replied — not sending step ${stepIdx} (${step.type})`);
                  await updateLeadStatus(campaign._id, lead._id, 'replied', undefined, {
                    stepType: 'message',
                    content: reply.text,
                    direction: 'inbound',
                  });
                  uiDirty = true;
                  continue;
                }
              }

              let content = stepMessage(step, lead, senderName, stepIdx, variableFallbacks);
              const language = step.aiLanguage || DEFAULT_AI_LANGUAGE;
              let postUrl: string | undefined;
              let aiResolved = false;
              const isPostStep = step.type === 'post_like' || step.type === 'post_comment';

              // ── Skip connection requests for existing 1st-degree connections ──
              // If the lead is already connected, do NOT send an invite; mark as
              // accepted and advance the step index. Record the skip — without it
              // a skipped lead is indistinguishable from one whose invite was sent.
              if (LIVE_EXTENSION && step.type === 'connection_request') {
                const isConnected = await checkLeadFirstDegree(campaign._id, lead);
                if (isConnected === true) {
                  console.log(`[Simulation] lead ${lead._id} already a 1st-degree connection — skipping invite step`);
                  await updateLeadStatus(campaign._id, lead._id, 'accepted', stepIdx + 1, {
                    stepType: step.type,
                    content: 'Already connected — invite skipped',
                    direction: 'outbound',
                  });
                  uiDirty = true;
                  continue;
                }
              }

              let postText = '';
              if (LIVE_EXTENSION && (isPostStep || needsPerLeadAi)) {
                const postResult = await fetchLeadLatestPost(lead);
                if (!('error' in postResult)) {
                  postUrl = postResult.postUrl;
                  postText = postResult.text ?? '';
                }
              }

              // For an AI step `content` still holds the PROMPT — generation is what turns
              // it into sendable text. Post text is context when we have it, never a
              // precondition: gating generation on it meant a lead whose latest post carries
              // no text (image, video, reshare without commentary — the only source is
              // update.commentary.text.text) had the raw prompt published as their comment.
              // Same rule as the proxy executor, which always generates for an AI step.
              if (needsPerLeadAi) {
                const generated = await generateCampaignMessage(campaign._id, lead._id, {
                  stepType: step.type,
                  content,
                  language,
                  ...(postText ? { postText } : {}),
                });
                if (!generated?.content) {
                  // Same rule as a failed send: a comment that cannot be generated
                  // must not park the lead on this step for the rest of the campaign.
                  if (POST_STEP_TYPES.includes(step.type)) {
                    if (registerPostStepFailure(postStepAttemptsRef.current, lead._id, stepIdx, 'ai_generation_failed') === 'skip') {
                      console.warn(`[Simulation] ${step.type} skipped for lead ${lead._id} (ai_generation_failed) — advancing to step ${stepIdx + 1}`);
                      await updateLeadStatus(
                        campaign._id, lead._id, lead.status, stepIdx + 1,
                        postStepSkipMessage(step.type, 'ai_generation_failed'), undefined, true,
                      );
                      uiDirty = true;
                    }
                    continue;
                  }
                  console.warn('[Simulation] AI generation failed, skipping step for', lead._id);
                  continue;
                }
                content = generated.content;
                aiResolved = true;
              }

              // Last line of defence: never send an AI step's own prompt. `content` starts
              // life as the prompt for every AI step, and only generation or a locked
              // finalContent replaces it — so any AI step reaching here ungenerated and
              // unlocked would publish the campaign's instructions to the lead.
              // Also covers AI message steps, which are not regenerated per lead: if their
              // finalContent was never locked in, the step is skipped rather than sent raw.
              // Connection requests are deliberately excluded: a note-less invite resolves
              // to 'Connection request sent', and their leftover-prompt edge case needs a
              // note-less send rather than a skip — holding the lead would be worse.
              const aiMode = (step.type === 'post_comment' && step.commentMode === 'ai')
                || (step.type === 'message' && step.messageMode === 'ai');
              const sendsRawAiPrompt = aiMode
                && !aiResolved
                && !getLeadStepContentOverride(lead, stepIdx)?.trim()
                && !step.finalContent?.trim();
              if (sendsRawAiPrompt) {
                console.error(`[Simulation] refusing to send unresolved AI content for lead ${lead._id} (step ${stepIdx}, ${step.type})`);
                continue;
              }

              // Execute via extension or proxy (no-op in simulation mode)
              // For CR notes: pass resolved note text (may be stripped by executor on quota).
              const result = await executeStep(
                campaign,
                lead,
                step,
                step.type === 'connection_request' && content === 'Connection request sent' ? '' : content,
              );
              if (!result.success) {
                if (result.reason === 'daily_limit_reached' || result.reason === 'weekly_limit_reached') {
                  console.log(`[Simulation] ${result.reason} for step type '${step.type}' — pausing this type for the tick`);
                  limitedTypes.add(step.type);
                  break;
                }
                if (
                  result.reason === 'note_quota_pause_noted'
                  || result.reason === 'linkedin_custom_invite_limit'
                ) {
                  console.log(`[Simulation] ${result.reason} — pausing noted / limited invites for this tick`);
                  limitedTypes.add(step.type);
                  break;
                }
                if (result.reason === 'note_quota_pause_campaign') {
                  console.log(`[Simulation] CR note quota exhausted — pausing campaign ${campaign._id}`);
                  await updateCampaign(campaign._id, { status: 'paused' });
                  uiDirty = true;
                  break;
                }
                if (result.reason === 'connection_not_accepted') {
                  // Message step but the lead is not a 1st-degree connection yet.
                  // No invite in the sequence → can never succeed; invite older than
                  // the acceptance window → give up. Otherwise retry on a later tick.
                  const hadInviteStep = sequence.slice(0, stepIdx).some(s => s.type === 'connection_request');
                  const inviteSentAt  = lead.lastContactedAt ? new Date(lead.lastContactedAt).getTime() : now;
                  const timedOut      = now - inviteSentAt > ACCEPTANCE_TIMEOUT_DAYS * 24 * 60 * 60 * 1000;
                  if (!hadInviteStep || timedOut) {
                    const reason = !hadInviteStep ? 'not_connected' : 'invite_not_accepted';
                    console.warn(`[Simulation] lead ${lead._id} failed: ${reason}`);
                    await updateLeadStatus(campaign._id, lead._id, 'failed', undefined, undefined, reason);
                    uiDirty = true;
                  }
                  continue;
                }
                // A post step is skipped, never failed and never retried forever:
                // a prospect with nothing to like or comment on is normal, and the
                // invite/message steps after it must still run.
                if (POST_STEP_TYPES.includes(step.type)) {
                  const reason = result.reason || 'unknown';
                  if (registerPostStepFailure(postStepAttemptsRef.current, lead._id, stepIdx, reason) === 'retry') {
                    continue;
                  }
                  console.warn(`[Simulation] ${step.type} skipped for lead ${lead._id} (${reason}) — advancing to step ${stepIdx + 1}`);
                  // Status stays as it is — the lead never reached post_liked /
                  // post_commented — and skipStep tells the API nothing was sent.
                  await updateLeadStatus(
                    campaign._id, lead._id, lead.status, stepIdx + 1,
                    postStepSkipMessage(step.type, reason), undefined, true,
                  );
                  uiDirty = true;
                  continue;
                }

                console.warn('[Simulation] step execution failed, skipping DB update for', lead._id, result.reason);
                continue;
              }

              if (result.reason === 'already_connected' && step.type === 'connection_request') {
                await updateLeadStatus(campaign._id, lead._id, 'accepted', stepIdx + 1, {
                  stepType: step.type,
                  content: 'Already connected — invite skipped',
                  direction: 'outbound',
                });
                uiDirty = true;
                continue;
              }

              const recordedContent = result.sentContent
                ?? (step.type === 'connection_request' ? (result.hasNote ? content : 'Connection request sent') : content);

              await updateLeadStatus(campaign._id, lead._id, nextStatus, stepIdx + 1, {
                stepType: step.type,
                content: recordedContent,
                direction: 'outbound',
                ...(step.type === 'connection_request' && { hasNote: !!result.hasNote }),
                ...(needsPerLeadAi && { aiGenerated: true, language }),
                ...(postUrl && { postUrl }),
              });
              postStepAttemptsRef.current.delete(`${lead._id}:${stepIdx}`);
              uiDirty = true;
            }
          }
          } // isActive — step execution only

          // ── DM sent → replied (inbound LN message after our DM) ─────────────
          // Poll even when later steps already ran (e.g. message → like → comment).
          const awaitingReply = leads.filter(l => isAwaitingCampaignMessageReply(l, sequence));

          for (const lead of awaitingReply) {
            if (LIVE_EXTENSION) {
              const sinceMs = getLeadMessageSentAtMs(lead)!;
              console.log(`[Simulation] reply check for lead ${lead._id} (${lead.publicIdentifier || lead.name})`);
              const reply = await checkLeadMessageReply(lead, sinceMs);
              if (reply?.text) {
                console.log(`[Simulation] lead ${lead._id} replied on LinkedIn`);
                const updated = await updateLeadStatus(campaign._id, lead._id, 'replied', undefined, {
                  stepType: 'message',
                  content: reply.text,
                  direction: 'inbound',
                });
                if (!updated?.lead) {
                  console.warn('[Simulation] failed to persist replied status for', lead._id, updated);
                } else {
                  uiDirty = true;
                }
              }
              continue;
            }

            // Dev/sim: optional fake reply (disabled in live mode).
            if (!pick(cfg.replyProbability)) continue;
            await updateLeadStatus(campaign._id, lead._id, 'replied', undefined, {
              stepType: 'message',
              content: "Thanks for reaching out! I'd be happy to connect.",
              direction: 'inbound',
            });
            uiDirty = true;
          }

          // converted / closed_lost / failed are set manually by the user — no auto-transition.
      }

      if (uiDirty) {
        window.dispatchEvent(new CustomEvent(SIM_EVENT));
      }
    } catch (err) {
      console.error('[Simulation] tick error:', err);
    }

    if (activeRef.current) scheduleTick();
  }, [getCampaigns, getCampaignLeads, updateLeadStatus, generateCampaignMessage, executeStep, checkLeadFirstDegree, checkLeadMessageReply, fetchLeadLatestPost, updateCampaign, scheduleTick]);

  runTickRef.current = runTick;

  useEffect(() => {
    if (!config.enabled) {
      activeRef.current = false;
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
      return;
    }
    activeRef.current = true;
    scheduleTick();
    console.log(`[Simulation] started — interval: ${config.minIntervalSeconds}–${config.maxIntervalSeconds}s`);
    return () => {
      activeRef.current = false;
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
      releaseLeadership();
      console.log('[Simulation] stopped');
    };
  }, [config.enabled, config.minIntervalSeconds, config.maxIntervalSeconds, scheduleTick]);
}
